import { FC } from "react";
import { CriminalReport } from "types/landlord/reports";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { Section } from "views/applications-id-report/components/Section";
import { KeyPairWrapper } from "./KeyPairWrapper";
import { DynamicKeyValue } from "views/applications-id-report/components/SmartRender/DynamicKeyValue";
import { formatDate } from "views/applications-id-report/utils";
import { Row } from "views/applications-id-report/components/Row";

type CriminalRecordsProps = {
  verificationId: number;
  results: CriminalReport["results"];
};

export const CriminalRecords: FC<CriminalRecordsProps> = ({
  results,
  verificationId
}) => {
  return (
    <>
      {results.map((result, resultIndex) => {
        const { subject, case_details, offenses } = result;

        return (
          <Section
            icon={
              <img
                src="/assets/criminal_report_logo.svg"
                alt="Criminal report icon"
              />
            }
            key={`report-${verificationId}-${resultIndex}`}
            sectionTitle={subject.category}
            maxWidth="none"
          >
            <ContentTitle title="Subject information">
              <div className="mb-6 flex gap-4">
                <img
                  src={
                    subject.image_url ||
                    "/assets/criminal_report_image_placeholder.svg"
                  }
                  alt="subject image"
                  className="w-40 rounded object-contain"
                />

                <div>
                  <p className="text-sm font-medium text-content-300">
                    Comments
                  </p>
                  <span className="mt-2.5 text-sm font-medium text-content-1000">
                    {case_details.comments || "N/A"}
                  </span>
                </div>
              </div>

              <KeyPairWrapper>
                <DynamicKeyValue
                  detailsKey="Full name"
                  value={subject.full_name}
                />
                <DynamicKeyValue detailsKey="Height" value={subject.height} />
              </KeyPairWrapper>

              <KeyPairWrapper>
                <Row title="Date of birth" value={formatDate(subject.dob)} />
                <DynamicKeyValue detailsKey="Skin tone" value={subject.race} />
              </KeyPairWrapper>

              <KeyPairWrapper>
                <DynamicKeyValue detailsKey="Sex" value={subject.sex} />
                <DynamicKeyValue
                  detailsKey="Military service"
                  value={subject.military_service}
                />
              </KeyPairWrapper>

              <KeyPairWrapper>
                <DynamicKeyValue
                  detailsKey="Hair color"
                  value={subject.hair_color}
                />
                <DynamicKeyValue
                  detailsKey="Age at crime"
                  value={subject.age}
                />
              </KeyPairWrapper>

              <KeyPairWrapper>
                <DynamicKeyValue
                  detailsKey="Eye color"
                  value={subject.eye_color}
                />
                <DynamicKeyValue
                  detailsKey="Scar/marks"
                  value={subject.scar_marks}
                />
              </KeyPairWrapper>

              <KeyPairWrapper>
                <DynamicKeyValue detailsKey="Weight" value={subject.weight} />
                <DynamicKeyValue detailsKey="Address" value={subject.address} />
              </KeyPairWrapper>

              <KeyPairWrapper>
                <DynamicKeyValue detailsKey="Alias" value={subject.alias} />
              </KeyPairWrapper>
            </ContentTitle>

            <ContentTitle title="Case details">
              <KeyPairWrapper>
                <DynamicKeyValue
                  detailsKey="Case number"
                  value={case_details.case_number}
                />
              </KeyPairWrapper>

              <KeyPairWrapper>
                <DynamicKeyValue
                  detailsKey="State"
                  value={case_details.state}
                />
              </KeyPairWrapper>

              <KeyPairWrapper>
                <DynamicKeyValue
                  detailsKey="Jurisdiction"
                  value={case_details.jurisdiction}
                />
              </KeyPairWrapper>
            </ContentTitle>

            {offenses.map((offense, offenseIndex) => (
              <ContentTitle
                title={`Offense ${offenseIndex + 1}`}
                key={`offense-${verificationId}-${offenseIndex}`}
              >
                <KeyPairWrapper>
                  <DynamicKeyValue detailsKey="Court" value={offense.court} />
                </KeyPairWrapper>
                <KeyPairWrapper>
                  <DynamicKeyValue
                    detailsKey="Case Type"
                    value={offense.case_type}
                  />
                </KeyPairWrapper>

                <KeyPairWrapper>
                  <DynamicKeyValue detailsKey="Status" value={offense.status} />
                </KeyPairWrapper>

                <KeyPairWrapper>
                  <DynamicKeyValue
                    detailsKey="Filing Date"
                    value={
                      offense.filing_date
                        ? formatDate(offense.filing_date)
                        : "--"
                    }
                  />
                </KeyPairWrapper>
                <KeyPairWrapper>
                  <DynamicKeyValue
                    detailsKey="Description"
                    value={offense.description}
                  />
                </KeyPairWrapper>

                <KeyPairWrapper hide={!offense.statute}>
                  <DynamicKeyValue
                    detailsKey="Statute"
                    value={offense.statute}
                  />
                </KeyPairWrapper>

                <KeyPairWrapper hide={!offense.conviction_date}>
                  <Row
                    title="Conviction Date"
                    value={formatDate(offense.conviction_date)}
                  />
                </KeyPairWrapper>

                <KeyPairWrapper hide={!offense.conviction_location}>
                  <DynamicKeyValue
                    detailsKey="Conviction Location"
                    value={offense.conviction_location}
                  />
                </KeyPairWrapper>

                <KeyPairWrapper hide={!offense.disposition}>
                  <DynamicKeyValue
                    detailsKey="Disposition"
                    value={offense.disposition}
                  />
                </KeyPairWrapper>

                <KeyPairWrapper hide={!offense.disposition_date}>
                  <Row
                    title="Disposition Date"
                    value={formatDate(offense.disposition_date)}
                  />
                </KeyPairWrapper>

                <KeyPairWrapper hide={!offense.offense_code}>
                  <DynamicKeyValue
                    detailsKey="Offense Code"
                    value={offense.offense_code}
                  />
                </KeyPairWrapper>

                <KeyPairWrapper hide={!offense.offense_date}>
                  <DynamicKeyValue
                    detailsKey="Offense Date"
                    value={formatDate(offense.offense_date)}
                  />
                </KeyPairWrapper>

                <KeyPairWrapper hide={!offense.charge_file_date}>
                  <DynamicKeyValue
                    detailsKey="Charge file date"
                    value={formatDate(offense.charge_file_date)}
                  />
                </KeyPairWrapper>

                <KeyPairWrapper hide={!offense.sentence_length}>
                  <DynamicKeyValue
                    detailsKey="Sentence Length"
                    value={offense.sentence_length}
                  />
                </KeyPairWrapper>

                <KeyPairWrapper hide={!offense.commitment_date}>
                  <DynamicKeyValue
                    detailsKey="Commitment Date"
                    value={formatDate(offense.commitment_date)}
                  />
                </KeyPairWrapper>

                <KeyPairWrapper hide={!offense.release_date}>
                  <DynamicKeyValue
                    detailsKey="Release Date"
                    value={formatDate(offense.release_date)}
                  />
                </KeyPairWrapper>
              </ContentTitle>
            ))}
          </Section>
        );
      })}
    </>
  );
};
